// The one place the SPA talks to the Worker API.
//
// Reproduces the surface of a Retool-generated backend hook so that ported
// components need no changes:
//
//   const { data, loading, error, trigger } = useGetFleet()
//   await trigger({ location: 'Soweto' }, { skipCache: true })
//
// Differences from Retool, all deliberate:
//   - `trigger` returns the result as well as setting `data`, because several
//     ported components already `await trigger(...)` and then read `.data` on
//     the next render. Returning the value removes that race for new code
//     without breaking the old pattern.
//   - a tiny in-memory cache, keyed on route + params, mirrors Retool's
//     implicit query caching so a page that mounts three components reading the
//     same endpoint does not fire three identical requests. `skipCache: true`
//     bypasses it, which is what every mutation's refresh path passes.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { supabase } from '../lib/supabase'
import { isReadRoute } from './routeKind'
import { readLimiter } from './requestQueue'
import {
  NETWORK_UNREACHABLE,
  RETRY_DELAY_MS,
  shouldRetryNetworkFailure,
  shouldRetryResponse,
  type ServerErrorBody,
} from './retryPolicy'
import {
  applyConfirmed,
  getConfirmedVersion,
  nextSeq,
  noteReadSettled,
  subscribeConfirmed,
} from './confirmedWrites'

export type BackendState<T> = {
  data: T | null
  loading: boolean
  // A STRING, not an Error. Retool's generated hooks surfaced `error` as a
  // string, and 30 of the ported components render it directly as a ReactNode
  // or pass it to a `string | null` prop. Exposing an Error object here would
  // mean editing 30 byte-identical copies to satisfy my runtime, which is
  // backwards: the runtime exists to match them.
  //
  // `trigger` still REJECTS with a real Error, so `catch (err)` blocks in the
  // ported code that read `err.message` keep working.
  error: string | null
}

type TriggerOptions = { skipCache?: boolean }

// Retool's generated hooks returned an object from `trigger()` carrying a
// `.result` promise, and the ported components use BOTH styles:
//
//   await trigger({ ... })            // pages/Fleet.tsx and most reads
//   await trigger({ ... }).result     // hooks/useInterviewDecisions.ts
//
// So `trigger` returns a promise that also carries `.result` pointing at
// itself. Both spellings therefore await the same settlement. Without this,
// every interview decision would throw "Cannot read properties of undefined
// (reading 'result')" at runtime, and nothing in a typecheck would catch it.
export type TriggerResult<T> = Promise<T> & { result: Promise<T> }

function withResult<T>(promise: Promise<T>): TriggerResult<T> {
  const hybrid = promise as TriggerResult<T>
  Object.defineProperty(hybrid, 'result', {
    value: promise,
    enumerable: false,
    configurable: true,
  })
  return hybrid
}

// Shared across hook instances so two components asking the same question in
// the same tick share one request.
const inflight = new Map<string, Promise<unknown>>()
const cache = new Map<string, { at: number; value: unknown }>()
const CACHE_TTL_MS = 15_000

export class BackendError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'BackendError'
    this.status = status
  }
}

// ---------------------------------------------------------------------------
// Holding a session across a screen left open
// ---------------------------------------------------------------------------
// The defect this replaces, found 26 August 2026. The Supabase access token
// lives 3,600 seconds. `autoRefreshToken` renews it on a timer, and every
// browser throttles timers in a hidden tab, so a screen left open past the hour
// woke holding a dead token. The old code read the resulting 401 and called
// `supabase.auth.signOut()`, which revokes the refresh token server-side. That
// turned a routine, fully recoverable expiry into a forced logout: filters gone,
// half-finished work gone, and the operator reasonably reporting it as "the app
// times out if you sit on one screen".
//
// Measured on two live sessions before the fix: the shortest gap between token
// renewals was 58 minutes on both, exactly as designed, but the average was 194
// and 470 minutes and the longest was 52 hours. Gaps that size are re-logins,
// not renewals.
//
// Two changes, and the second is the one that matters:
//   1. renew before the request instead of on a timer, because a timer is a
//      guess about when the token will be needed and a pre-flight check is a
//      fact;
//   2. treat a 401 as one retry rather than as a verdict.
//
// WHY RETRYING A 401 CANNOT REPLAY A WRITE, which is the obvious objection and
// the reason this is safe: `resolveUser` runs in api/src/index.ts before the
// request body is read, before `openSql`, and before the handler. A 401
// therefore proves nothing executed. A handler's own refusal is a 400, never a
// 401, so a refusal can never be retried by this path.

const REFRESH_MARGIN_MS = 90_000

// One refresh at a time, shared by every caller. Supabase rotates the refresh
// token on use, so two concurrent refreshes race and the loser presents a token
// that is already spent: that is a way to destroy a session while trying to save
// it. A page mounting six reads at once must produce one refresh, not six.
let refreshInflight: Promise<string | null> | null = null

function refreshOnce(): Promise<string | null> {
  if (!refreshInflight) {
    refreshInflight = supabase.auth
      .refreshSession()
      .then(({ data, error }) => (error ? null : (data.session?.access_token ?? null)))
      .catch(() => null)
      .finally(() => {
        refreshInflight = null
      })
  }
  return refreshInflight
}

// The token to send, renewed first when it is close enough to expiry that the
// request would land after it. Null means there is genuinely no session, which
// the caller must not paper over.
export async function freshToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  const session = data.session
  if (!session) return null
  const expiresAtMs = (session.expires_at ?? 0) * 1000
  if (expiresAtMs - Date.now() > REFRESH_MARGIN_MS) return session.access_token
  // Fall back to the existing token if the renewal fails: it may still have
  // seconds left, and the retry below is the real safety net.
  return (await refreshOnce()) ?? session.access_token
}

function send(route: string, params: unknown, token: string): Promise<Response> {
  return fetch(`/api/${route}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params ?? {}),
  })
}

// ONE RETRY FOR A DROPPED CONNECTION, alongside the one for an expired token.
//
// The defect, from the screen on 30 August 2026: "write CONNECTION_CLOSED
// ...hyperdrive.local:5432" in a panel, and the two access banners, after most
// actions. Every API call opens a connection through Hyperdrive and closes it
// again, drops on that path are ordinary, and nothing absorbed them: the Worker
// answered 400 with the driver's text, which is indistinguishable from a
// handler's refusal, so nothing could retry and the operator reloaded the page.
//
// The Worker now takes the first retry itself. This is the second net, for a
// fault between the browser and Cloudflare, which the Worker never sees.
//
// The rule for what may be resent is in retryPolicy.ts and is deliberately
// narrow: a read always, a write only when the server states that nothing ran.
// The same argument the 401 retry above rests on, and it must stay that strict,
// because a resent approval is a driver paid twice.
async function post<T>(route: string, params: unknown): Promise<T> {
  const token = await freshToken()
  if (!token) {
    throw new BackendError('You are not signed in.', 401)
  }
  const isRead = isReadRoute(route)

  // A READ WAITS FOR A SLOT. A WRITE NEVER DOES.
  //
  // Measured on the live API, the degraded-permission failures are purely a
  // function of how many requests are in the air: zero at ten, seven per cent at
  // thirty, and Postgres sitting at 15 connections of 60 throughout, which means
  // the ceiling is Hyperdrive's pool and not the database. The Cockpit's own
  // refresh wave is what walks into it. See requestQueue.ts for the numbers.
  const release = isRead ? await readLimiter.acquire() : null
  try {
    return await postThroughRetries(route, params, token, isRead)
  } finally {
    release?.()
  }
}

async function postThroughRetries<T>(
  route: string,
  params: unknown,
  token: string,
  isRead: boolean,
): Promise<T> {
  let sendToken = token
  let res: Response | null = null
  let body: ServerErrorBody & { data?: T } = {}
  let unreadable = false

  for (let attempt = 0; ; attempt++) {
    res = null
    body = {}
    unreadable = false

    try {
      res = await send(route, params, sendToken)
    } catch {
      // fetch rejected: no response exists to read a status from.
      res = null
    }

    // Renew once and resend, exactly as before. Only on the first attempt: a 401
    // that survives a renewal is a real authentication failure, and the branch
    // below ends the session for it.
    if (res && res.status === 401 && attempt === 0) {
      const renewed = await refreshOnce()
      if (renewed) {
        sendToken = renewed
        try {
          res = await send(route, params, renewed)
        } catch {
          res = null
        }
      }
    }

    if (res) {
      try {
        body = (await res.json()) as typeof body
      } catch {
        unreadable = true
      }
    }

    const retryable =
      res === null
        ? shouldRetryNetworkFailure(isRead)
        : shouldRetryResponse(res.status, unreadable ? {} : body, isRead)

    if (retryable && attempt === 0) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
      continue
    }
    break
  }

  if (!res) {
    throw new BackendError(NETWORK_UNREACHABLE, 0)
  }
  if (unreadable) {
    throw new BackendError(`The server returned an unreadable response (${res.status}).`, res.status)
  }

  if (!res.ok) {
    // A 401 that survived a renewal is a real authentication failure rather than
    // an expiry, so the session genuinely is over and the app returns to the
    // sign-in screen. Only here, never on the first 401.
    if (res.status === 401) {
      void supabase.auth.signOut()
    }
    // The message is the handler's or the database's verbatim text. Preserving
    // it is the point: "A material correction changes what a driver is paid and
    // can only be approved by an admin" is the actual product.
    throw new BackendError(body.error ?? `Request failed (${res.status}).`, res.status)
  }
  return body.data as T
}

// ---------------------------------------------------------------------------
// Live refresh
// ---------------------------------------------------------------------------
// The problem this solves. Every read was cached for 15 seconds and nothing ever
// cleared that cache: `invalidateBackendCache` below was exported on 21 August
// and never called from anywhere. So a vehicle changed on Vehicle Allocation
// stayed stale on Fleet, on Performance and on Today until the entry aged out,
// and a change made by another operator never appeared at all until you
// navigated. Ported components each re-triggered their OWN reads with
// `skipCache: true` after a write, which is why this was survivable and also why
// it was invisible: the screen you were standing on looked right.
//
// What happens now, in one sentence: any successful write clears the read cache
// and every mounted read refetches itself, in this tab and in every other tab
// this browser has open.
//
// What this deliberately is NOT. It is not a live feed of other people's
// changes. Two operators editing at the same time still only see each other's
// work on the next write, or when they come back to the window. True push would
// mean either polling a change feed on the Worker, or a Supabase Realtime
// subscription straight from the browser. The second is barred today: the whole
// point of this architecture is that the browser holds a session token and
// nothing else, and no view sets `security_invoker = true`, so a realtime
// subscription would be a second, unauthorised read path into Postgres. Read
// docs/DRIVER-AUTH.md before anyone reaches for it.

let dataVersion = 0
const versionListeners = new Set<() => void>()
let pendingBump: ReturnType<typeof setTimeout> | null = null

// Why a write and a wake are told apart, and why one route sits out the write.
//
// The defect this addresses, reported from the screen on 26 August 2026: after
// about five task completions in a row the board replaced itself with an
// "ask an admin" card. useCapabilities.tsx now refuses to turn a failed check
// into a lockout, which is the fix. This is the other half: making the failure
// far less likely by not asking the same question ten times for one click.
//
// Every successful write clears the cache and refetches every mounted read. On
// the task board that is getTaskBoard, getTaskPeople, getTaskIngestRuns,
// getTaskLinkOptions and getCapabilities: five requests. Four of those handlers
// call resolveCapabilities themselves, so one tick of one checkbox costs about
// nine database round trips. The write is broadcast to every other tab of the
// same browser, which runs the same wave again, and an operator with the Cockpit
// open twice therefore pays about eighteen. Five completions in quick succession
// overlap, and the request that loses is enough to degrade the answer that gates
// fourteen pages.
//
// getCapabilities is excluded from the write wave for the reason already written
// down in useCapabilities.tsx: who can do what changes when a row in
// cockpit_access changes, which is roughly never, and certainly not because
// somebody ticked a task. It still refreshes when the operator comes back to the
// window, and `retry()` on the banner still asks immediately. Access changes
// remain a database row that takes effect on the next page load, exactly as
// CLAUDE.md section 8 says.
type BumpReason = 'write' | 'wake'
let bumpReason: BumpReason = 'wake'

const NO_REFRESH_ON_WRITE = new Set<string>(['shared/getCapabilities'])

// Writes that change nothing any other screen shows, so they do not start the
// refresh wave at all. Live Fleet's camera start and poll write only the
// viewing record in vehicle_camera_session. On 30 September 2026, fifteen tiles
// polling every 1.5 seconds made about ten writes a second, each one refetching
// every mounted read in every tab, and the database connections began to drop
// ("The connection to the database dropped while this was being saved").
// discipleship/openPageHelp writes only a row noting that a page has no manual
// section yet. Opening the help panel must not refresh every other screen.
const SILENT_WRITES = new Set<string>(['fleet/startCameraStream', 'fleet/pollCameraStream', 'discipleship/openPageHelp'])

function getDataVersion(): number {
  return dataVersion
}

function subscribeDataVersion(listener: () => void): () => void {
  versionListeners.add(listener)
  return () => {
    versionListeners.delete(listener)
  }
}

// Coalesced on a short trailing timer. A batch approve fires a dozen writes and
// must produce one refresh wave, not a dozen.
function bumpDataVersion(reason: BumpReason = 'write'): void {
  // A wake outranks a write: if both land in the same window the reads that sit
  // out a write must still refresh, because coming back to the window is the one
  // moment the operator is looking at possibly stale access.
  if (reason === 'wake') bumpReason = 'wake'
  else if (!pendingBump) bumpReason = 'write'
  if (pendingBump) return
  pendingBump = setTimeout(() => {
    pendingBump = null
    dataVersion += 1
    for (const listener of [...versionListeners]) listener()
  }, 120)
}

// Other tabs of the same browser. Operators keep Vehicle Allocation open beside
// Fleet, and a change in one has to land in the other.
//
// Guarded on `window`, not on BroadcastChannel. Node has BroadcastChannel too,
// and an open channel holds the event loop open: constructing one at module load
// made a server-side render of this tree hang instead of exit, which is how this
// was found. Anything that renders these components outside a browser, now or
// later, must not acquire a handle it never closes.
const channel =
  typeof window === 'undefined' || typeof BroadcastChannel === 'undefined'
    ? null
    : new BroadcastChannel('fleetly.data')
// A HIDDEN TAB DOES NOT REFETCH; IT REMEMBERS THAT IT SHOULD.
//
// Nobody is reading a background tab, so spending a full request wave on it buys
// nothing and doubles the load on the visible tab's database at the exact moment
// that tab is busiest. The cache is still cleared, so nothing stale can be served
// from it, and the refresh happens when the tab is actually looked at.
let refreshDeferredWhileHidden = false

channel?.addEventListener('message', () => {
  cache.clear()
  if (typeof document !== 'undefined' && document.hidden) {
    refreshDeferredWhileHidden = true
    return
  }
  bumpDataVersion('write')
})

function announceWrite(): void {
  cache.clear()
  bumpDataVersion('write')
  // Never send the payload. Another tab may be a different page with different
  // permissions; all it needs to know is that something changed.
  channel?.postMessage({ at: Date.now() })
}

// Coming back to the window is the moment an operator looks at the screen again,
// so it is the cheapest honest place to catch someone else's change. Throttled,
// because alt-tabbing is not a data event.
const REVALIDATE_THROTTLE_MS = 10_000
let lastRevalidateAt = 0

function revalidateStaleReads(): void {
  // A refresh deferred while hidden is owed and bypasses the throttle: it is the
  // update this tab deliberately skipped, not an alt-tab.
  const owed = refreshDeferredWhileHidden
  if (!owed && Date.now() - lastRevalidateAt < REVALIDATE_THROTTLE_MS) return
  refreshDeferredWhileHidden = false
  lastRevalidateAt = Date.now()
  cache.clear()
  bumpDataVersion('wake')
}

// Coming back to a tab is also the moment the throttled refresh timer has most
// likely already missed its slot, so the token is renewed here as well as the
// reads. Cheap: freshToken returns the current token untouched unless it is
// within 90 seconds of expiry.
function onWake(): void {
  revalidateStaleReads()
  void freshToken()
}

if (typeof window !== 'undefined') {
  window.addEventListener('focus', onWake)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onWake()
  })
}

// Default generic is `any`, deliberately. Retool's generated hooks typed their
// payloads as `any`, and the ported components rely on it: DataFixes.tsx reads
// `.conflict`, `.person_id` and `.full_name` straight off a response. Defaulting
// to `unknown` would be safer in the abstract and would require editing ported
// files to add type parameters, which is the one thing this port does not do.
// Add a type argument at the call site when writing NEW code.
export function useBackend<T = any>(route: string) {
  const [state, setState] = useState<BackendState<T>>({ data: null, loading: false, error: null })
  // Guards against a slow response from an earlier trigger overwriting a
  // faster, newer one.
  const generation = useRef(0)
  // The last question this hook actually asked, so a refresh can ask it again
  // with the filters the operator is currently looking at rather than with the
  // defaults it mounted with. Reads only: a write is never replayed, because
  // replaying a write is how you pay someone twice.
  const lastRead = useRef<{ params: unknown } | null>(null)

  const run = useCallback(
    async (params: unknown, options: TriggerOptions): Promise<T> => {
      const key = `${route}:${JSON.stringify(params ?? {})}`
      const mine = ++generation.current
      // A write must never be served from the cache, never be deduplicated
      // against another in-flight write, and never be cached afterwards. The
      // cache and the in-flight map existed to stop three components asking one
      // question three times; applied to a write they mean an operator clicking
      // Approve twice within the TTL gets the first response back and believes
      // the second click landed when the server never saw it. Reads and writes
      // are therefore separated here, not just labelled.
      const isRead = isReadRoute(route)
      if (isRead) lastRead.current = { params }
      // Taken before the request leaves, because what a held confirmation needs
      // to know is whether this read STARTED after the write was confirmed, not
      // whether it finished after it. See hooks/confirmedWrites.ts.
      const startedSeq = nextSeq()
      setState((s) => ({ ...s, loading: true, error: null }))

      try {
        let value: T
        let fromNetwork = false
        const cached = isRead && !options.skipCache ? cache.get(key) : undefined
        if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
          value = cached.value as T
        } else {
          let pending =
            isRead && !options.skipCache ? (inflight.get(key) as Promise<T> | undefined) : undefined
          if (!pending) {
            pending = post<T>(route, params)
            fromNetwork = true
            if (isRead) {
              inflight.set(key, pending)
              void pending.finally(() => inflight.delete(key))
            }
          }
          value = await pending
          if (isRead) cache.set(key, { at: Date.now(), value })
        }
        if (mine === generation.current) {
          setState({ data: value, loading: false, error: null })
          // Only a first-hand answer counts as the reload catching up. A cached
          // or deduplicated value may have been fetched before the write landed,
          // and treating it as the reload is how the new value flickers back to
          // the old one for a frame.
          if (isRead && fromNetwork) noteReadSettled(route, startedSeq)
        }
        // Only after the server has confirmed it. Announcing an attempted write
        // would refresh every screen to the same figures and look like success.
        if (!isRead && !SILENT_WRITES.has(route)) announceWrite()
        return value
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        if (mine === generation.current) {
          // Store the message; reject with the Error. See BackendState above.
          setState({ data: null, loading: false, error: error.message })
        }
        throw error
      }
    },
    [route],
  )

  const trigger = useCallback(
    (params: unknown = {}, options: TriggerOptions = {}): TriggerResult<T> =>
      withResult(run(params, options)),
    [run],
  )

  // Refetch when anything in the app writes, when another tab writes, or when
  // the operator comes back to the window. A hook that has never been triggered
  // has no question to re-ask, so it stays quiet, and a refresh is itself a read
  // and therefore cannot trigger another round.
  const version = useSyncExternalStore(subscribeDataVersion, getDataVersion, getDataVersion)
  const seenVersion = useRef(version)

  useEffect(() => {
    if (version === seenVersion.current) return
    // Consumed either way, so a route that sits out a write does not then treat
    // the NEXT wake as already seen.
    seenVersion.current = version
    if (bumpReason === 'write' && NO_REFRESH_ON_WRITE.has(route)) return
    const last = lastRead.current
    if (!last) return
    void run(last.params, { skipCache: true }).catch(() => {
      // run() has already put the message in state for the screen to show. A
      // background refresh must not become an unhandled rejection on top of it.
    })
  }, [version, run, route])

  // Render any confirmed change the reload has not caught up with yet. Reads
  // only: a write hook's `data` is the server's answer to that write and must
  // never be dressed up. `applyConfirmed` returns the identical reference when
  // nothing applies, so a page that never declares one is untouched by this.
  const confirmedVersion = useSyncExternalStore(
    subscribeConfirmed,
    getConfirmedVersion,
    getConfirmedVersion,
  )
  void confirmedVersion
  const data = isReadRoute(route) ? applyConfirmed(route, state.data) : state.data

  return { ...state, data, trigger }
}

/** Clears the read cache. Call after a mutation that invalidates many reads. */
export function invalidateBackendCache(routePrefix?: string): void {
  if (!routePrefix) {
    cache.clear()
    return
  }
  for (const key of [...cache.keys()]) {
    if (key.startsWith(routePrefix)) cache.delete(key)
  }
}
